import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, ArrowDown, ArrowUp, Minus } from "lucide-react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

const ROWS = [
  { key: "critical_count", label: "Kritická zjištění", betterLower: true },
  { key: "high_count", label: "Závažná zjištění", betterLower: true },
  { key: "medium_count", label: "Středně závažná zjištění", betterLower: true },
  { key: "unverifiable_count", label: "Neověřitelné", betterLower: true },
  { key: "mandatory_failed", label: "Povinné požadavky se zjištěním", betterLower: true },
  { key: "ok_count", label: "Splněná kritéria", betterLower: false },
];

export default function ComparePage() {
  const { aId, bId } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/audits/${aId}`), api.get(`/audits/${bId}`)])
      .then(([ra, rb]) => { setA(ra.data); setB(rb.data); })
      .catch(() => {});
  }, [aId, bId]);

  if (!a || !b) {
    return <Layout><div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div></Layout>;
  }

  const older = new Date(a.created_at) <= new Date(b.created_at) ? a : b;
  const newer = older === a ? b : a;

  const Delta = ({ row }) => {
    const d = (newer.summary?.[row.key] ?? 0) - (older.summary?.[row.key] ?? 0);
    if (d === 0) return <span className="flex items-center gap-1 text-slate-400"><Minus className="h-3.5 w-3.5" /> beze změny</span>;
    const improved = row.betterLower ? d < 0 : d > 0;
    return (
      <span className={`flex items-center gap-1 font-semibold ${improved ? "text-emerald-700" : "text-rose-700"}`}>
        {d < 0 ? <ArrowDown className="h-3.5 w-3.5" /> : <ArrowUp className="h-3.5 w-3.5" />}
        {d > 0 ? `+${d}` : d} {improved ? "(zlepšení)" : "(zhoršení)"}
      </span>
    );
  };

  return (
    <Layout>
      <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="mb-4 text-slate-600 hover:text-slate-900" data-testid="compare-back-button">
        <ArrowLeft className="mr-1.5 h-4 w-4" /> Zpět na přehled
      </Button>
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Porovnání auditů</h1>
      <p className="mt-1 text-sm text-slate-500">Jak se posunulo vaše ŠVP mezi dvěma kontrolami.</p>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="compare-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3 font-semibold">Ukazatel</th>
              <th className="px-5 py-3 font-semibold">
                Starší audit<br /><span className="normal-case font-normal">{new Date(older.created_at).toLocaleDateString("cs-CZ")} · {older.document_filename}</span>
              </th>
              <th className="px-5 py-3 font-semibold">
                Novější audit<br /><span className="normal-case font-normal">{new Date(newer.created_at).toLocaleDateString("cs-CZ")} · {newer.document_filename}</span>
              </th>
              <th className="px-5 py-3 font-semibold">Změna</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {ROWS.map((r) => (
              <tr key={r.key}>
                <td className="px-5 py-3 font-medium text-slate-800">{r.label}</td>
                <td className="px-5 py-3 text-lg font-bold text-slate-900">{older.summary?.[r.key] ?? 0}</td>
                <td className="px-5 py-3 text-lg font-bold text-slate-900">{newer.summary?.[r.key] ?? 0}</td>
                <td className="px-5 py-3 text-xs"><Delta row={r} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate(`/audits/${older.id}`)} data-testid="open-older-audit">Otevřít starší audit</Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/audits/${newer.id}`)} data-testid="open-newer-audit">Otevřít novější audit</Button>
      </div>
    </Layout>
  );
}
